import { PropsWithChildren, createContext, useContext, useState } from "react";

import { useCheckMigrationCompatibilityLazyQuery } from "@/apollo/types";
import { useToast } from "@/providers/ToastProvider";
import { useSocket } from "@/socket/SocketContext";
import { toBase64 } from "@/utils/encoding";

import { MigrationCompatibilityResult } from "./DataWarehouseMigrationProvider";

const DataWarehouseMigrationCompatibilityContext = createContext<{
  checkMigrationCompatibility: (
    values: Record<string, any>,
  ) => Promise<MigrationCompatibilityResult | undefined>;
  compatibilityResult: MigrationCompatibilityResult;
  compatibilityCheckLoading: boolean;
} | null>(null);

export function useDataWarehouseMigrationCompatibilityContext() {
  const context = useContext(DataWarehouseMigrationCompatibilityContext);
  if (!context) {
    throw new Error(
      "useDataWarehouseMigrationContext must be used within a DataWarehouseMigrationProvider",
    );
  }
  return context;
}

export function DatawarehouseMigrationCompatibilityProvider(
  props: PropsWithChildren<{ dwConnectionPayload: Record<string, any> }>,
) {
  const compatibilityCheck = useCompatibilityCheck();
  return (
    <DataWarehouseMigrationCompatibilityContext.Provider
      value={compatibilityCheck}
    >
      {props.children}
    </DataWarehouseMigrationCompatibilityContext.Provider>
  );
}

export function useCompatibilityCheck() {
  const toast = useToast();
  const [compatibilityResult, setCompatibilityResult] =
    useState<MigrationCompatibilityResult>({
      existingTables: [],
      existingViews: [],
      datasetsInWrongLocation: [],
      importedItems: [],
      compatible: true,
    });
  const [compatibilityCheckLoading, setCompatibilityCheckLoading] =
    useState(false);

  const [compatibilityCheckQuery] = useCheckMigrationCompatibilityLazyQuery({
    fetchPolicy: "network-only",
  });

  const ws = useSocket();

  const checkMigrationCompatibility: (
    values: Record<string, any>,
  ) => Promise<MigrationCompatibilityResult | undefined> = async (
    config: Record<string, any>,
  ) => {
    setCompatibilityCheckLoading(true);
    const { data, error } = await compatibilityCheckQuery({
      variables: {
        createConnectionInput: {
          integrationId: "bigquery",
          label: "_datawarehouse",
          payload: toBase64(config),
        },
      },
    });

    if (!data?.checkMigrationCompatibility || error) {
      toast(
        "Failed to check compatibility of migration",
        "Something went wrong",
        "error",
      );
      setCompatibilityCheckLoading(false);
      return;
    }

    return new Promise<MigrationCompatibilityResult | undefined>((resolve) => {
      ws.on("data-warehouse-migration:compatibility-check-finished", (resp) => {
        setCompatibilityCheckLoading(false);
        setCompatibilityResult(resp.payload);

        ws.off("data-warehouse-migration:compatibility-check-failed");
        ws.off("data-warehouse-migration:compatibility-check-finished");

        resolve(resp.payload);
      });

      ws.on("data-warehouse-migration:compatibility-check-failed", (resp) => {
        toast(
          "Failed to check compatibility of migration",
          resp.payload.errorMessage || "Something went wrong",
          "error",
        );
        setCompatibilityCheckLoading(false);

        ws.off("data-warehouse-migration:compatibility-check-failed");
        ws.off("data-warehouse-migration:compatibility-check-finished");

        resolve(undefined);
      });
    });
  };

  return {
    checkMigrationCompatibility,
    compatibilityCheckLoading,
    compatibilityResult,
  };
}
